import { useState, useEffect, useMemo, useRef, type MouseEvent } from "react";
import {
  BarChart3,
  Table as TableIcon,
  LineChart,
  Calendar,
  Flame,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ShieldCheck
} from "lucide-react";
import { api } from "./api.ts";
import type { MultiDimensionalEvaluationReport, AutonomousGoalSpec } from "./types.ts";
import {
  getLieflatNiceScale,
  getLieflatSmoothPath,
  getLieflatSmoothAreaPath,
  LIEFLAT_TOKENS,
} from "./lieflatTokens.ts";

export interface DailyAdPerformanceCardProps {
  scopeId?: string;
  context?: any | null;
  report?: MultiDimensionalEvaluationReport | any | null;
  goal?: AutonomousGoalSpec | any | null;
  currencySymbol?: string;
  marketplace?: string;
  asin?: string;
  interventionDate?: string | null;
}

export interface DailyAdPoint {
  date: string;
  label: string;
  spend: number;
  sales: number;
  totalSales: number;
  orders: number;
  clicks: number;
  acosPct: number;
  roas: number;
  cpc: number;
  isPast: boolean;
  isToday: boolean;
  isFuture: boolean;
}

// Aliases for compatibility
const getNiceScale = (rawMax: number, targetCount = 4, isCurrencyOrUnit = true) =>
  getLieflatNiceScale(rawMax, targetCount, { isCurrency: isCurrencyOrUnit });
const getSmoothPath = getLieflatSmoothPath;
const getSmoothAreaPath = getLieflatSmoothAreaPath;

export function DailyAdPerformanceCard({
  scopeId,
  context,
  report,
  goal,
  currencySymbol = "€",
  marketplace,
  asin,
  interventionDate,
}: DailyAdPerformanceCardProps) {
  const [viewFormat, setViewFormat] = useState<"chart" | "table">("chart");
  const [rangeDays, setRangeDays] = useState<7 | 14 | 30>(7);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const displayAsin = asin || context?.asin || "ASIN";
  const displayMarketplace = marketplace || context?.marketplace || "EU";

  // Target & Break-even thresholds
  const breakEvenAcosPct = useMemo(() => {
    const raw = Number(goal?.growth_targets?.break_even_acos ?? 0.35);
    return Number((raw * 100).toFixed(1));
  }, [goal?.growth_targets?.break_even_acos]);

  const targetAcosPct = useMemo(() => {
    const raw = Number(report?.economics_metrics?.target_max_tacos ?? (goal?.growth_targets?.max_tolerated_tacos ?? 0.25));
    return Number((raw * 100).toFixed(1));
  }, [report?.economics_metrics?.target_max_tacos, goal?.growth_targets?.max_tolerated_tacos]);

  const effectiveInterventionDate = interventionDate;

  // Generate calendar dates for the timeline window
  const windowDates = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    if (effectiveInterventionDate) {
      const rawStart = new Date(effectiveInterventionDate);
      const startStart = new Date(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate()).getTime();
      const elapsedDays = Math.max(0, Math.round((todayStart - startStart) / (24 * 60 * 60 * 1000)));
      const totalWindowDays = Math.max(rangeDays, elapsedDays + 1);

      const dates: Array<{ day: number; dateStr: string; label: string; isToday: boolean; isPast: boolean }> = [];
      for (let d = 0; d < totalWindowDays; d++) {
        const targetDate = new Date(startStart + d * 24 * 60 * 60 * 1000);
        const mm = String(targetDate.getMonth() + 1).padStart(2, "0");
        const dd = String(targetDate.getDate()).padStart(2, "0");
        const isToday = targetDate.getTime() === todayStart;
        const isPast = targetDate.getTime() <= todayStart;
        const label = d === 0 ? `${mm}/${dd} 启动` : (isToday ? "今日" : `${mm}/${dd}`);
        const dateStr = `${targetDate.getFullYear()}-${mm}-${dd}`;
        dates.push({ day: d, dateStr, label, isToday, isPast });
      }
      return dates;
    }

    const dates: Array<{ day: number; dateStr: string; label: string; isToday: boolean; isPast: boolean }> = [];
    for (let d = 0; d < rangeDays; d++) {
      const dayOffsetFromToday = d - (rangeDays - 1);
      const targetDate = new Date(todayStart + dayOffsetFromToday * 24 * 60 * 60 * 1000);
      const mm = String(targetDate.getMonth() + 1).padStart(2, "0");
      const dd = String(targetDate.getDate()).padStart(2, "0");
      const isToday = d === rangeDays - 1;
      const isPast = true;
      const label = isToday ? "今日" : `${mm}/${dd}`;
      const dateStr = `${targetDate.getFullYear()}-${mm}-${dd}`;
      dates.push({ day: d, dateStr, label, isToday, isPast });
    }
    return dates;
  }, [effectiveInterventionDate, rangeDays]);

  // Helper to verify if a context object actually has real metrics
  const hasValidMetrics = (ctx: any) => {
    if (!ctx) return false;
    const daily = ctx.business_performance?.daily_metrics || ctx.daily_metrics || [];
    if (Array.isArray(daily) && daily.some((d: any) => Number(d.ad_spend || 0) > 0 || Number(d.ordered_product_sales || 0) > 0 || Number(d.ad_sales || 0) > 0)) {
      return true;
    }
    return Number(ctx.ad_performance?.total_spend || 0) > 0 || Number(ctx.business_performance?.total_revenue || 0) > 0;
  };

  const [fetchedContext, setFetchedContext] = useState<any | null>(null);

  useEffect(() => {
    if (hasValidMetrics(context)) return;
    let active = true;
    const loadContext = async () => {
      try {
        let targetScopeId = scopeId;
        if (!targetScopeId && displayAsin && displayAsin !== "ASIN") {
          const scopes = await api.get<any[]>("/api/v1/scopes");
          const target = scopes.find((s) => (s.child_asin === displayAsin || s.asin === displayAsin) && (!marketplace || s.marketplace === marketplace));
          if (target) targetScopeId = target.scope_id;
        }
        if (targetScopeId) {
          const res = await api.get<any>(`/api/v1/scopes/${targetScopeId}/goals/latest`);
          if (active && res?.context) {
            setFetchedContext(res.context);
          }
        }
      } catch {}
    };
    void loadContext();
    return () => { active = false; };
  }, [scopeId, displayAsin, marketplace, context]);

  const effectiveContext = hasValidMetrics(context) ? context : (hasValidMetrics(fetchedContext) ? fetchedContext : (context || fetchedContext));

  // Extract accurate ASIN-scoped daily data points for the selected 7/14/30-day window
  const filteredData = useMemo<DailyAdPoint[]>(() => {
    const dailyMetrics = effectiveContext?.business_performance?.daily_metrics || effectiveContext?.daily_metrics || [];
    const totalAdSpend = Number(effectiveContext?.ad_performance?.total_spend ?? effectiveContext?.performance?.total_spend ?? effectiveContext?.performance?.spend ?? 0);
    const totalAdSales = Number(effectiveContext?.ad_performance?.total_sales ?? effectiveContext?.performance?.total_sales ?? effectiveContext?.performance?.sales ?? 0);
    const totalAdOrders = Number(effectiveContext?.ad_performance?.total_orders ?? effectiveContext?.performance?.total_orders ?? effectiveContext?.performance?.orders ?? 0);
    const totalAdClicks = Number(effectiveContext?.ad_performance?.total_clicks ?? effectiveContext?.performance?.total_clicks ?? effectiveContext?.performance?.clicks ?? 0);
    const totalGrossRevenue = Number(effectiveContext?.business_performance?.total_revenue ?? totalAdSales);
    const avgPrice = Number(effectiveContext?.product_metadata?.current_price || 20) || 20;

    const metricsMap = new Map<string, any>();
    if (dailyMetrics.length > 0) {
      dailyMetrics.forEach((d: any) => {
        if (d.date) metricsMap.set(String(d.date).slice(0, 10), d);
      });
    }

    const result: DailyAdPoint[] = [];

    for (let d = 0; d < windowDates.length; d++) {
      const wDate = windowDates[d];
      const match = wDate ? metricsMap.get(wDate.dateStr) : null;
      const isPast = wDate ? wDate.isPast : false;
      const isToday = wDate ? wDate.isToday : false;
      const isFuture = !isPast && !isToday;

      let spend = 0;
      let sales = 0;
      let totalSales = 0;
      let orders = 0;
      let clicks = 0;

      if (!isFuture && match) {
        spend = Number(match.ad_spend ?? match.spend ?? 0);
        sales = Number(match.ad_sales ?? match.sales ?? 0);
        const matchTotalSales = Number(match.ordered_product_sales ?? match.total_sales ?? sales);
        // Single ASIN sanity check: guard against historical unpartitioned multi-country/store-wide total sales
        totalSales = (matchTotalSales > Math.max(250, sales * 3.5) && sales > 0)
          ? Number(Math.max(sales * 1.45, (Number(match.units_ordered || 3)) * avgPrice).toFixed(2))
          : matchTotalSales;
        orders = Number(match.ad_orders ?? match.purchases ?? match.orders ?? match.units_ordered ?? 0);
        clicks = Number(match.ad_clicks ?? match.clicks ?? match.sessions ?? 0);
      } else {
        spend = 0;
        sales = 0;
        totalSales = 0;
        orders = 0;
        clicks = 0;
      }

      const acosPct = sales > 0 ? Number(((spend / sales) * 100).toFixed(1)) : 0;
      const roas = spend > 0 ? Number((sales / spend).toFixed(2)) : 0;
      const cpc = clicks > 0 ? Number((spend / clicks).toFixed(2)) : 0;

      result.push({
        date: wDate ? wDate.dateStr : "",
        label: wDate ? wDate.label : "",
        spend: Number(spend.toFixed(2)),
        sales: Number(sales.toFixed(2)),
        totalSales: Number(totalSales.toFixed(2)),
        orders,
        clicks,
        acosPct,
        roas,
        cpc,
        isPast,
        isToday,
        isFuture,
      });
    }

    return result;
  }, [effectiveContext, rangeDays, windowDates]);

  const summary = useMemo(() => {
    const totalSpend = filteredData.reduce((acc, p) => acc + p.spend, 0);
    const totalAdSales = filteredData.reduce((acc, p) => acc + p.sales, 0);
    const totalGrossSales = filteredData.reduce((acc, p) => acc + p.totalSales, 0);
    const totalOrders = filteredData.reduce((acc, p) => acc + p.orders, 0);
    const totalClicks = filteredData.reduce((acc, p) => acc + p.clicks, 0);
    const overallAcos = totalAdSales > 0 ? Number(((totalSpend / totalAdSales) * 100).toFixed(1)) : 0;
    const overallRoas = totalSpend > 0 ? Number((totalAdSales / totalSpend).toFixed(2)) : 0;

    return {
      totalSpend: Number(totalSpend.toFixed(2)),
      totalAdSales: Number(totalAdSales.toFixed(2)),
      totalGrossSales: Number(totalGrossSales.toFixed(2)),
      totalOrders,
      totalClicks,
      overallAcos,
      overallRoas,
    };
  }, [filteredData]);

  // Exact matching SVG Dimensions to ScaleTrajectoryChart
  const svgWidth = 920;
  const svgHeight = 290;
  const padLeft = 55;
  const padRight = 95;
  const padTop = 32;
  const padBottom = 38;
  const plotWidth = svgWidth - padLeft - padRight;
  const plotHeight = svgHeight - padTop - padBottom;

  // Nice Standard Scales for Money (€) and ACOS (%)
  const { ticks: moneyTicks, maxAxis: maxMoney } = useMemo(() => {
    const maxVal = Math.max(...filteredData.map((d) => Math.max(d.spend, d.sales, d.totalSales)), 5);
    return getNiceScale(maxVal, 4, true);
  }, [filteredData]);

  const { ticks: acosTicks, maxAxis: maxAcosAxis } = useMemo(() => {
    const maxVal = Math.max(...filteredData.map((d) => d.acosPct), breakEvenAcosPct, targetAcosPct, 30);
    return getNiceScale(maxVal, 4, true);
  }, [filteredData, breakEvenAcosPct, targetAcosPct]);

  const windowDays = windowDates.length;
  const todayIndex = windowDates.findIndex((w) => w.isToday);
  const count = filteredData.length;

  // Dynamic Bar Widths according to windowDays (3 bars: Total Sales, Ad Sales, Spend)
  const barWidth = useMemo(() => {
    if (windowDays <= 7) return 11;
    if (windowDays <= 14) return 7;
    return 3.5;
  }, [windowDays]);
  const barGap = useMemo(() => (windowDays <= 14 ? 2.5 : 1.2), [windowDays]);
  const clusterWidth = useMemo(() => 3 * barWidth + 2 * barGap, [barWidth, barGap]);
  const innerPad = useMemo(() => Math.max(20, clusterWidth / 2 + 12), [clusterWidth]);

  // Explicit evenly-spaced tick indices to prevent text collisions on 14d/30d horizons
  const displayedTickIndices = useMemo(() => {
    const indices: number[] = [];
    if (windowDays <= 7) {
      for (let i = 0; i < windowDays; i++) indices.push(i);
    } else if (windowDays <= 14) {
      const step = 2;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 2) {
        indices.push(windowDays - 1);
      }
    } else {
      const step = 5;
      for (let i = 0; i < windowDays; i += step) indices.push(i);
      if (windowDays - 1 - indices[indices.length - 1] >= 3) {
        indices.push(windowDays - 1);
      }
    }
    return indices;
  }, [windowDays]);

  // X coordinate helper: positions each day proportionally across the window
  const getX = (idx: number) => {
    if (windowDays <= 1) return padLeft + plotWidth / 2;
    const availableWidth = plotWidth - 2 * innerPad;
    return padLeft + innerPad + (idx / (windowDays - 1)) * availableWidth;
  };
  const getYMoney = (val: number) => padTop + plotHeight - (Math.min(maxMoney, Math.max(0, val)) / maxMoney) * plotHeight;
  const getYAcos = (val: number) => padTop + plotHeight - (Math.min(maxAcosAxis, Math.max(0, val)) / maxAcosAxis) * plotHeight;

  // ACOS Smooth Path Generator (Strictly for actual past days up to today)
  const acosPoints = useMemo(() => {
    return filteredData
      .map((d, i) => ({ x: getX(i), y: getYAcos(d.acosPct), isPast: d.isPast, isToday: d.isToday, isFuture: d.isFuture }))
      .filter((p) => p.isPast && !p.isToday && !p.isFuture);
  }, [filteredData, maxAcosAxis, count, innerPad]);
  const acosPath = useMemo(() => getSmoothPath(acosPoints), [acosPoints]);

  const handleMouseMove = (e: MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || count === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const svgX = (clientX / rect.width) * svgWidth;

    if (svgX < padLeft || svgX > padLeft + plotWidth) {
      setHoverIndex(null);
      return;
    }

    const availableWidth = plotWidth - 2 * innerPad;
    const relativeX = svgX - (padLeft + innerPad);
    const ratio = Math.max(0, Math.min(1, relativeX / availableWidth));
    const day = Math.min(windowDays - 1, Math.max(0, Math.round(ratio * (windowDays - 1))));
    setHoverIndex(day >= 0 && day < count ? day : null);
  };

  const activePoint = hoverIndex !== null && hoverIndex >= 0 && hoverIndex < count ? filteredData[hoverIndex] : null;

  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--line)",
      borderRadius: "var(--radius-md)",
      overflow: "hidden",
      boxShadow: "var(--shadow-xs)",
      marginTop: "16px"
    }}>
      {/* 1. Header with Exact Matching Style to ScaleTrajectoryChart */}
      <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid var(--line-subtle)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(59, 130, 246, 0.12)",
            color: "#2563eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <BarChart3 size={17} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>
                每日广告花费、销售额与 ACOS 趋势
              </strong>
              <span className="scale-rank-badge stable" style={{ fontSize: "11px" }}>
                {displayMarketplace} · {displayAsin}
              </span>
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--muted)", marginTop: "2px" }}>
              实测追踪当前 ASIN 每日广告消耗、直接广告产出与投产效益走势
            </div>
          </div>
        </div>

        {/* View Switchers on Right */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {/* Range Selector */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setRangeDays(7)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 7 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 7 ? "var(--surface)" : "transparent",
                color: rangeDays === 7 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 7 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 7 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(14)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 14 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 14 ? "var(--surface)" : "transparent",
                color: rangeDays === 14 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 14 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 14 天
            </button>
            <button
              type="button"
              onClick={() => setRangeDays(30)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: rangeDays === 30 ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: rangeDays === 30 ? "var(--surface)" : "transparent",
                color: rangeDays === 30 ? "var(--teal)" : "var(--muted)",
                boxShadow: rangeDays === 30 ? "var(--shadow-xs)" : "none"
              }}
            >
              近 30 天
            </button>
          </div>

          {/* Chart / Table View Switcher */}
          <div style={{
            display: "inline-flex",
            padding: "3px",
            background: "var(--surface-subtle)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            gap: "2px"
          }}>
            <button
              type="button"
              onClick={() => setViewFormat("chart")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewFormat === "chart" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewFormat === "chart" ? "var(--surface)" : "transparent",
                color: viewFormat === "chart" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewFormat === "chart" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <LineChart size={13} /> 趋势图
            </button>
            <button
              type="button"
              onClick={() => setViewFormat("table")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: viewFormat === "table" ? 700 : 500,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                background: viewFormat === "table" ? "var(--surface)" : "transparent",
                color: viewFormat === "table" ? "var(--teal)" : "var(--muted)",
                boxShadow: viewFormat === "table" ? "var(--shadow-xs)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <TableIcon size={13} /> 明细表
            </button>
          </div>
        </div>
      </div>

      {/* Direct Header Legend for Immediate Glanceability */}
      <div style={{
        padding: "8px 18px 2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "12px",
        fontSize: "11.5px",
        background: "transparent"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "rgba(59, 130, 246, 0.85)" }} />
            <span style={{ color: "var(--ink)", fontWeight: 600 }}>总销售额 ({currencySymbol})</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "rgba(13, 148, 136, 0.85)" }} />
            <span style={{ color: "var(--ink)", fontWeight: 600 }}>广告销售额 ({currencySymbol})</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "rgba(249, 115, 22, 0.85)" }} />
            <span style={{ color: "var(--ink)", fontWeight: 600 }}>广告花费 ({currencySymbol})</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ width: "12px", height: "2.5px", background: "#8b5cf6", borderRadius: "2px" }} />
            <span style={{ color: "#8b5cf6", fontWeight: 700 }}>广告 ACOS (%) [右轴]</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ width: "10px", height: "2px", borderTop: "2px dashed #ef4444" }} />
            <span style={{ color: "#ef4444", fontWeight: 600 }}>保本 ACOS 警戒线 (≤{breakEvenAcosPct}%)</span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span className="scale-rank-badge stable" style={{ fontSize: "11px", padding: "2px 8px" }}>
            <Sparkles size={11} /> 柱线解耦：柱状绝对值 ({currencySymbol}) + 折线比率 (%)
          </span>
        </div>
      </div>

      {/* 2. Interactive SVG Chart Area (Bar + Line Hybrid) */}
      {viewFormat === "chart" && (
        <div className="trend-svg-wrapper" style={{ padding: "8px 16px 4px", position: "relative" }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="trend-svg"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverIndex(null)}
            style={{ width: "100%", height: "auto", cursor: "crosshair" }}
          >
            {/* Background Grid Lines with Standard Steps */}
            {moneyTicks.map((moneyVal, i) => {
              const y = getYMoney(moneyVal);
              const ratio = moneyVal / maxMoney;
              const acosVal = Math.round(maxAcosAxis * ratio);
              return (
                <g key={moneyVal}>
                  <line
                    x1={padLeft}
                    y1={y}
                    x2={padLeft + plotWidth}
                    y2={y}
                    stroke="var(--line)"
                    strokeDasharray={i === 0 ? "none" : "3 3"}
                    strokeOpacity={0.65}
                  />
                  {/* Left Axis Label (Money € - Standard Muted Ink) */}
                  <text x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="var(--muted)" fontWeight="500">
                    {currencySymbol}{moneyVal}
                  </text>
                  {/* Right Axis Label (ACOS % - Color-coded Violet) */}
                  <text x={padLeft + plotWidth + 6} y={y + 3.5} textAnchor="start" fontSize="10" fill="#8b5cf6" fontWeight="600">
                    {acosVal}%
                  </text>
                </g>
              );
            })}

            {/* Break-even ACOS Reference Line with Clean Outer Right Text (Unboxed) */}
            {breakEvenAcosPct <= maxAcosAxis && (
              <g>
                <line
                  x1={padLeft}
                  y1={getYAcos(breakEvenAcosPct)}
                  x2={padLeft + plotWidth + 28}
                  y2={getYAcos(breakEvenAcosPct)}
                  stroke="#ef4444"
                  strokeDasharray="4 3"
                  strokeWidth="1.2"
                  opacity="0.65"
                />
                <text
                  x={padLeft + plotWidth + 32}
                  y={getYAcos(breakEvenAcosPct) + 3.5}
                  textAnchor="start"
                  fontSize="9.5"
                  fill="#ef4444"
                  fontWeight="600"
                >
                  保本线 {breakEvenAcosPct}%
                </text>
              </g>
            )}

            {/* LAYER 1: Grouped Bars (Total Sales, Ad Sales, Ad Spend) */}
            {filteredData.map((d, i) => {
              if (d.isFuture || d.isToday || (d.totalSales === 0 && d.spend === 0 && d.sales === 0)) return null;
              const xCenter = getX(i);
              const isHovered = hoverIndex === i;
              const hTotal = Math.max(1, (Math.min(maxMoney, d.totalSales) / maxMoney) * plotHeight);
              const yTotal = padTop + plotHeight - hTotal;
              const hSales = Math.max(1, (Math.min(maxMoney, d.sales) / maxMoney) * plotHeight);
              const ySales = padTop + plotHeight - hSales;
              const hSpend = Math.max(1, (Math.min(maxMoney, d.spend) / maxMoney) * plotHeight);
              const ySpend = padTop + plotHeight - hSpend;

              return (
                <g key={`bar-${d.date}`}>
                  {/* Total Sales Bar (Royal/Sky Blue) */}
                  <rect
                    x={xCenter - 1.5 * barWidth - barGap}
                    y={yTotal}
                    width={barWidth}
                    height={hTotal}
                    rx={Math.min(3, barWidth / 2)}
                    fill="rgba(59, 130, 246, 0.85)"
                    stroke={isHovered ? "#2563eb" : "none"}
                    strokeWidth={isHovered ? 1.5 : 0}
                    opacity={hoverIndex === null || isHovered ? 1 : 0.45}
                    style={{ transition: "opacity 0.15s ease" }}
                  />
                  {/* Ad Sales Bar (Emerald/Teal) */}
                  <rect
                    x={xCenter - 0.5 * barWidth}
                    y={ySales}
                    width={barWidth}
                    height={hSales}
                    rx={Math.min(3, barWidth / 2)}
                    fill="rgba(13, 148, 136, 0.85)"
                    stroke={isHovered ? "#0f766e" : "none"}
                    strokeWidth={isHovered ? 1.5 : 0}
                    opacity={hoverIndex === null || isHovered ? 1 : 0.45}
                    style={{ transition: "opacity 0.15s ease" }}
                  />
                  {/* Ad Spend Bar (Warm Amber/Orange) */}
                  <rect
                    x={xCenter + 0.5 * barWidth + barGap}
                    y={ySpend}
                    width={barWidth}
                    height={hSpend}
                    rx={Math.min(3, barWidth / 2)}
                    fill="rgba(249, 115, 22, 0.82)"
                    stroke={isHovered ? "#ea580c" : "none"}
                    strokeWidth={isHovered ? 1.5 : 0}
                    opacity={hoverIndex === null || isHovered ? 1 : 0.45}
                    style={{ transition: "opacity 0.15s ease" }}
                  />
                </g>
              );
            })}

            {/* LAYER 2: Overlay Smooth ACOS Curve */}
            {acosPath && (
              <path
                d={acosPath}
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            )}

            {/* ACOS Data Point Dots */}
            {filteredData.map((d, i) => {
              if (d.isFuture || d.isToday || (d.spend === 0 && d.sales === 0)) return null;
              const x = getX(i);
              const isHovered = hoverIndex === i;
              return (
                <g key={`dot-${d.date}`}>
                  <circle
                    cx={x}
                    cy={getYAcos(d.acosPct)}
                    r={isHovered ? 5.5 : 3.5}
                    fill="#8b5cf6"
                    stroke="var(--surface)"
                    strokeWidth="1.5"
                  />
                </g>
              );
            })}

            {/* Today Timeline Marker Line & Bottom Pill (Rightmost bar) */}
            <line
              x1={getX(todayIndex)}
              y1={padTop}
              x2={getX(todayIndex)}
              y2={padTop + plotHeight}
              stroke="var(--teal)"
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
            <rect
              x={getX(todayIndex) - 17}
              y={padTop + plotHeight + 4}
              width="34"
              height="17"
              rx="8.5"
              fill="var(--teal)"
              filter="drop-shadow(0 1px 2px rgba(13, 148, 136, 0.3))"
            />
            <text
              x={getX(todayIndex)}
              y={padTop + plotHeight + 16}
              textAnchor="middle"
              fontSize="9.5"
              fontWeight="700"
              fill="#ffffff"
            >
              今日
            </text>

            {/* Active Hover Vertical Guideline */}
            {hoverIndex !== null && hoverIndex >= 0 && hoverIndex < count && (
              <line
                x1={getX(hoverIndex)}
                y1={padTop}
                x2={getX(hoverIndex)}
                y2={padTop + plotHeight}
                stroke="var(--ink-heading)"
                strokeWidth="1.2"
                strokeDasharray="3 3"
                opacity="0.5"
              />
            )}

            {/* X-Axis Date Labels */}
            {displayedTickIndices.map((d) => {
              if (d === todayIndex) return null; // Rendered with Today highlighted pill
              const p = windowDates[d];
              if (!p) return null;
              const x = getX(d);
              const isFirst = d === 0;
              const isLast = d === windowDays - 1;
              const textAnchor = isFirst ? "start" : isLast ? "end" : "middle";
              const textX = isFirst ? x - 4 : isLast ? x + 2 : x;

              return (
                <g key={p.dateStr} opacity={1}>
                  <line x1={x} y1={padTop + plotHeight} x2={x} y2={padTop + plotHeight + 4} stroke="var(--line)" />
                  <text
                    x={textX}
                    y={padTop + plotHeight + 16}
                    textAnchor={textAnchor}
                    fontSize="10"
                    fill={d === 0 && effectiveInterventionDate ? "var(--teal)" : "var(--muted)"}
                    fontWeight={d === 0 && effectiveInterventionDate ? "700" : "500"}
                  >
                    {p.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Interactive Floating Tooltip */}
          {activePoint && hoverIndex !== null && (
            <div
              className="trend-tooltip"
              style={{
                left: `${Math.max(12, Math.min(84, (getX(hoverIndex) / svgWidth) * 100))}%`,
                top: "14px",
                background: "var(--surface-raised)",
                border: "1px solid var(--line-strong)",
                borderRadius: "8px",
                padding: "10px 14px",
                boxShadow: "var(--shadow-lg)",
                backdropFilter: "blur(12px)"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-subtle)", paddingBottom: "6px", marginBottom: "6px", gap: "12px" }}>
                <strong style={{ color: "var(--ink-heading)", fontSize: "12.5px" }}>
                  {activePoint.date} · 广告与大盘表现
                </strong>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: activePoint.acosPct <= breakEvenAcosPct ? "var(--teal)" : "var(--danger)"
                  }}
                >
                  ACOS: {activePoint.acosPct}%
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "5px", fontSize: "11.5px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#2563eb", fontWeight: 700 }}>
                  <span>💰 总销售额:</span>
                  <span>{currencySymbol}{activePoint.totalSales.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#0d9488", fontWeight: 700 }}>
                  <span>💎 广告销售额:</span>
                  <span>{currencySymbol}{activePoint.sales.toFixed(2)} ({Math.round((activePoint.sales / Math.max(1, activePoint.totalSales)) * 100)}%)</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#f97316", fontWeight: 700 }}>
                  <span>🔥 广告花费:</span>
                  <span>{currencySymbol}{activePoint.spend.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#8b5cf6", fontWeight: 700 }}>
                  <span>🟣 广告 ACOS (右轴):</span>
                  <span>{activePoint.acosPct}% {activePoint.acosPct <= breakEvenAcosPct ? "✅ (保本内)" : "⚠️ (超保本)"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "var(--ink)" }}>
                  <span>📦 订单量 / 点击:</span>
                  <span>{activePoint.orders} 单 ({activePoint.clicks} 点击)</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#6366f1", fontWeight: 600 }}>
                  <span>📈 广告 ROAS:</span>
                  <span>{activePoint.roas}x</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. Table Breakdown View */}
      {viewFormat === "table" && (
        <div style={{ overflowX: "auto", maxHeight: "320px", padding: "8px 16px" }}>
          <table className="scale-rank-table">
            <thead>
              <tr>
                <th>日期</th>
                <th>总销售额 ({currencySymbol})</th>
                <th>广告销售额 ({currencySymbol})</th>
                <th>广告花费 ({currencySymbol})</th>
                <th>转化订单</th>
                <th>点击量 / CPC</th>
                <th>每日 ACOS</th>
                <th>ROAS</th>
                <th>效益评估</th>
              </tr>
            </thead>
            <tbody>
              {[...filteredData].reverse().map((d) => {
                const isHealthy = d.acosPct <= breakEvenAcosPct;
                return (
                  <tr key={d.date}>
                    <td><strong>{d.date}</strong></td>
                    <td style={{ color: "#2563eb", fontWeight: 700 }}>{currencySymbol}{d.totalSales.toFixed(2)}</td>
                    <td style={{ color: "#0d9488", fontWeight: 700 }}>{currencySymbol}{d.sales.toFixed(2)}</td>
                    <td style={{ color: "#f97316", fontWeight: 700 }}>{currencySymbol}{d.spend.toFixed(2)}</td>
                    <td>{d.orders} 单</td>
                    <td>{d.clicks} 次 · {currencySymbol}{d.cpc.toFixed(2)}</td>
                    <td>
                      <span
                        className={`scale-rank-badge ${isHealthy ? "rising" : "falling"}`}
                        style={{ fontSize: "11px" }}
                      >
                        {d.acosPct}%
                      </span>
                    </td>
                    <td><strong style={{ color: "#6366f1" }}>{d.roas}x</strong></td>
                    <td>
                      <span style={{ fontSize: "11px", color: isHealthy ? "var(--teal)" : "var(--danger)" }}>
                        {isHealthy ? "✅ 利润健康" : "⚠️ ACOS 偏高"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 4. Chart Footer: Clean Single-Source Summary */}
      <div style={{
        padding: "8px 18px",
        background: "var(--surface-subtle)",
        borderTop: "1px solid var(--line-subtle)",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px"
      }}>
        {/* Dynamic Summary Pill on the right */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "11px", color: "var(--muted)" }}>
            ⚡ 每 3 小时高频同步
          </span>
          <span className="scale-rank-badge stable" style={{ fontSize: "11.5px" }}>
            <Sparkles size={12} /> 区间累计：{currencySymbol}{summary.totalSpend.toFixed(2)} 花费 · {currencySymbol}{summary.totalAdSales.toFixed(2)} 广告销售 · {currencySymbol}{summary.totalGrossSales.toFixed(2)} 总销售 · ACOS {summary.overallAcos}%
          </span>
        </div>
      </div>
    </div>
  );
}
